import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { TbEdit } from 'react-icons/tb';
import { RiDeleteBin6Line } from 'react-icons/ri';
import TableLoader from '../../../utils/TableLoader';
import defaultImage from '../../../utils/DefaultImage';
import PaginationComponent from '../../../utils/PaginationComponent';
import ConfirmationDialog from '../../../utils/ConfirmationDialog';
import EditWineQuantityModal from './EditWineQuantityModal';
import viewIcon from "../../../assets/admin/view.svg";

const ProposalPreview = (props) => {
    const { hasPermission } = props || {};
    const { slug2 } = useParams();
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const [proposal, setProposal] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Modal states
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [wineToDelete, setWineToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [wineToEdit, setWineToEdit] = useState(null);
    const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);

    // Pagination for wines
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchProposalDetails = useCallback(async () => {
        if (!slug2) return;
        setIsLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/api/v1/proposals/${slug2}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // console.log("Proposal Details Response:", response.data);
            setProposal(response.data);
        } catch (error) {
            console.error("Error fetching proposal details:", error);
        } finally {
            setIsLoading(false);
        }
    }, [slug2, apiUrl, token]);

    useEffect(() => {
        fetchProposalDetails();
    }, [fetchProposalDetails]);

    // Clean image url in case of markdown formatting artifacts
    const cleanImageUrl = (url) => {
        if (!url || url === "null") return null;
        let clean = url;
        if (clean.startsWith("[")) {
            clean = clean.split('"]')[0].replace('["', '');
        }
        return clean;
    };

    // Delete wine handler
    const confirmDeleteWine = async () => {
        if (!wineToDelete) return;

        if (!proposal?.proposal_wines || proposal.proposal_wines.length <= 1) {
            toast.warning("Impossibile eliminare l'ultimo vino. La proposal deve contenere almeno un vino.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        if (hasPermission && !hasPermission("Proposals", "remove")) {
            toast.error("Non hai i permessi per eliminare questo vino.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        const productId = wineToDelete.wine_id || wineToDelete.wine?.id || wineToDelete.product_id;
        const targetProposalId = slug2 || proposal?.id;

        if (!productId || !targetProposalId) {
            toast.error("Identificativo del vino o della proposal non valido.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        setIsDeleting(true);
        try {
            const response = await axios.delete(
                `${apiUrl}/api/v1/proposals/${targetProposalId}/wines/${productId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const successMsg = response.data?.message || "Vino rimosso con successo dalla proposal!";
            toast.success(successMsg, { theme: "colored" });
            setWineToDelete(null);

            // Adjust page if deleting last item on the page
            if (currentWines.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            }

            await fetchProposalDetails();
        } catch (error) {
            console.error("Error removing wine from proposal:", error);
            const msg = error.response?.data?.message || "Errore durante l'eliminazione del vino.";
            toast.error(msg, { theme: "colored" });
        } finally {
            setIsDeleting(false);
        }
    };

    // Save wine quantity handler
    const handleSaveWineQuantity = async (wineItem, newQuantity) => {
        if (!wineItem) return;
        if (hasPermission && !hasPermission("Proposals", "edit")) {
            toast.error("Non hai i permessi per modificare questa proposal.", { theme: "colored" });
            setWineToEdit(null);
            return;
        }
        const targetProposalId = slug2 || proposal?.id;
        const productId = wineItem.wine_id || wineItem.wine?.id || wineItem.product_id;

        if (!targetProposalId || !productId) {
            toast.error("Identificativo del vino o della proposal non valido.", { theme: "colored" });
            return;
        }

        setIsUpdatingQuantity(true);
        try {
            const response = await axios.patch(
                `${apiUrl}/api/v1/proposals/${targetProposalId}/wines/${productId}`,
                { quantity: Number(newQuantity) },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const successMsg = response.data?.message || "Quantità aggiornata con successo!";
            toast.success(successMsg, { theme: "colored" });
            setWineToEdit(null);
            await fetchProposalDetails();
        } catch (error) {
            console.error("Error updating wine quantity:", error);
            const msg = error.response?.data?.message || "Errore durante l'aggiornamento della quantità.";
            toast.error(msg, { theme: "colored" });
        } finally {
            setIsUpdatingQuantity(false);
        }
    };

    // Paginate wines list
    const totalWines = proposal?.proposal_wines?.length || 0;
    const startIndex = (page - 1) * rowsPerPage;
    const currentWines = proposal?.proposal_wines?.slice(startIndex, startIndex + rowsPerPage) || [];

    return (
        <div className="w-full relative">
            <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center items-start gap-3 mt-3 mb-5 w-full">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold poppins-medium text-[#303030]">
                    Dettagli Proposal {proposal?.restaurant?.company_name ? `- ${proposal.restaurant.company_name}` : ""}
                </h3>
                <button
                    onClick={() => navigate('/admin/proposals')}
                    className="rounded-full py-[6px] border-[2px] bg-[#fff] border-[#F2C3CA] px-5 text-[#8B2433] text-sm font-medium shadow-sm poppins-regular whitespace-nowrap shrink-0 hover:bg-[#8B2433] hover:text-white transition-colors cursor-pointer"
                >
                    Torna indietro
                </button>
            </div>
            
            <div className="w-auto dash-table-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow overflow-x-auto">
                <table className="md:w-full w-[700px] text-left wine_table">
                    <thead>
                        <tr className="border-b border-[#8B2433]">
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Sr.no</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Immagine</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome Vino</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Quantità</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Prezzo (B2B)</th>
                            <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Azione</th>
                        </tr>
                    </thead>
                    {isLoading ? (
                        <TableLoader />
                    ) : (
                        <tbody>
                            {currentWines && currentWines.length > 0 ? (
                                currentWines.map((item, index) => (
                                    <tr key={item.id} className="border-b border-[#8B2433]/12">
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {startIndex + index + 1}
                                        </td>
                                        <td className="pr-2 py-4">
                                            <div 
                                                className="thumbnail_image rounded-lg w-[50px] h-[60px] bg-white p-1 flex align-center justify-center cursor-pointer hover:shadow-md transition-shadow"
                                                onClick={() => setSelectedImage(cleanImageUrl(item?.wine?.image) || defaultImage)}
                                            >
                                                <img
                                                    className="w-full p-1 h-full object-contain border border-[#68172394] rounded-lg"
                                                    src={cleanImageUrl(item?.wine?.image) || defaultImage}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultImage;
                                                    }}
                                                    alt="Wine"
                                                />
                                            </div>
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {item.wine?.name ? (
                                                <div className="relative group inline-block">
                                                    <span className="select-text cursor-text">
                                                        {item.wine.name.length > 25 
                                                            ? `${item.wine.name.slice(0, 25)}...` 
                                                            : item.wine.name}
                                                    </span>
                                                    {item.wine.name.length > 25 && (
                                                        <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[70] bottom-full left-0 mb-2 w-max max-w-[320px] bg-[#303030] text-white text-xs rounded-lg py-2 px-3 shadow-xl whitespace-normal break-words pointer-events-auto select-text">
                                                            {item.wine.name}
                                                            <div className="absolute top-full left-4 border-4 border-transparent border-t-[#303030]"></div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            {item.quantity}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            € {item.b2b_price_snapshot}
                                        </td>
                                        <td className="py-4 px-2 text-left poppins-medium text-[#303030]">
                                            <div className="flex items-center gap-2">
                                                {(Boolean(hasPermission?.("Proposals", "view")) || !hasPermission) && (
                                                    <button
                                                        onClick={() => setSelectedDetails(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Vedi Dettagli Vino"
                                                    >
                                                        <img src={viewIcon} alt="view details" className="w-5 h-5" />
                                                    </button>
                                                )}
                                                {Boolean(hasPermission?.("Proposals", "edit")) && (
                                                    <button
                                                        onClick={() => setWineToEdit(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Modifica Quantità Vino"
                                                    >
                                                        <TbEdit size={"20px"} className="text-[#8B2433]" />
                                                    </button>
                                                )}
                                                {Boolean(hasPermission?.("Proposals", "remove")) && (
                                                    <button
                                                        onClick={() => setWineToDelete(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Elimina vino dalla proposal"
                                                    >
                                                        <RiDeleteBin6Line className="w-5 h-5 text-[#8B2433]" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-4">Nessun vino trovato</td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>
            {proposal?.proposal_wines && proposal.proposal_wines.length > 0 && (
                <PaginationComponent
                    page={page}
                    totalPages={totalWines}
                    rowsPerPage={rowsPerPage}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                />
            )}

            {/* Image Preview Modal */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" 
                    onClick={() => setSelectedImage(null)}
                >
                    <div 
                        className="relative p-2 bg-white rounded-xl shadow-2xl" 
                        onClick={e => e.stopPropagation()}
                    >
                        <button 
                            className="absolute -top-3 -right-3 bg-[#8B2433] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-md hover:bg-red-800 transition-colors" 
                            onClick={() => setSelectedImage(null)}
                        >
                            ✕
                        </button>
                        <img 
                            src={selectedImage} 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = defaultImage;
                            }}
                            alt="Wine Preview" 
                            className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg border border-[#8B2433]/20" 
                        />
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {selectedDetails && (
                <div 
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" 
                    onClick={() => setSelectedDetails(null)}
                >
                    <div 
                        className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-xl overflow-hidden border border-[#8B2433]/20 transform transition-all" 
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="bg-[#8B2433] px-4 md:px-6 py-4 flex justify-between items-center text-white shrink-0">
                            <h3 className="text-xl font-semibold poppins-medium">Dettagli Vino</h3>
                            <button 
                                className="text-white hover:text-gray-200 text-2xl font-bold leading-none" 
                                onClick={() => setSelectedDetails(null)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="p-4 md:p-6 flex flex-col gap-5 overflow-y-auto premium-scrollbar">
                            <div className="border-b pb-4 poppins-regular text-center md:text-left shrink-0">
                                <h4 className="text-2xl md:text-3xl font-semibold text-[#8B2433] poppins-medium">{selectedDetails?.wine?.name || 'N/A'}</h4>
                                <p className="text-xs md:text-sm text-gray-500 font-medium mt-1 uppercase tracking-wider">{selectedDetails?.wine?.producer || 'Nessun produttore'}</p>
                            </div>
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                                <div className="w-full md:w-1/3 flex shrink-0">
                                    <div className="bg-[#FBFBFB] p-4 rounded-xl border border-[#8B2433]/20 shadow-sm w-full h-full flex justify-center items-center min-h-[200px] md:min-h-[300px]">
                                        <img 
                                            src={cleanImageUrl(selectedDetails?.wine?.image) || defaultImage} 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = defaultImage;
                                            }}
                                            alt="Wine" 
                                            className="max-h-48 md:max-h-64 object-contain rounded-lg mix-blend-multiply transition-transform hover:scale-105 duration-300"
                                        />
                                    </div>
                                </div>
                                <div className="w-full md:w-2/3 flex flex-col poppins-regular">
                                    <style>{`
                                        .premium-scrollbar::-webkit-scrollbar { width: 6px; }
                                        .premium-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                                        .premium-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                                        .premium-scrollbar::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                                    `}</style>
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-3 text-sm mt-2 md:pr-3 overflow-y-visible md:overflow-y-auto md:max-h-[45vh] premium-scrollbar">
                                        {[
                                            { label: "Tipologia", value: selectedDetails?.wine?.product_type },
                                            { label: "Nazione", value: selectedDetails?.wine?.nation },
                                            { label: "Vitigno", value: selectedDetails?.wine?.grape_variety },
                                            { label: "Riferimento", value: selectedDetails?.wine?.reference },
                                            { label: "Box Content", value: selectedDetails?.wine?.box_content },
                                            { label: "Tipo", value: selectedDetails?.wine?.type },
                                            { label: "Annata", value: selectedDetails?.wine?.vintage_year },
                                            { label: "Formato", value: selectedDetails?.wine?.bottle_format },
                                            { label: "Etichetta", value: selectedDetails?.wine?.label },
                                            { label: "Temp. Servizio", value: selectedDetails?.wine?.serving_temperature },
                                            { label: "Grad. Alcolica", value: selectedDetails?.wine?.alcoholic_strength },
                                            { label: "EAN13", value: selectedDetails?.wine?.ean13 },
                                            { label: "UPC", value: selectedDetails?.wine?.upc },
                                            { label: "Categoria", value: selectedDetails?.wine?.category },
                                            { label: "Territorio", value: selectedDetails?.wine?.territory },
                                            { label: "Cru", value: selectedDetails?.wine?.cru },
                                            { label: "Terreno", value: selectedDetails?.wine?.soil_geology },
                                            { label: "Tipo Impianto", value: selectedDetails?.wine?.plant_type },
                                            { label: "Prod. Annua", value: selectedDetails?.wine?.annual_production },
                                            { label: "Resa", value: selectedDetails?.wine?.yield },
                                            { label: "Affinamento", value: selectedDetails?.wine?.container_refinement },
                                            { label: "Fermentazione", value: selectedDetails?.wine?.fermentation },
                                            { label: "Invecchiamento", value: selectedDetails?.wine?.aging },
                                            { label: "Età Viti", value: selectedDetails?.wine?.age_of_vines },
                                            { label: "Lieviti", value: selectedDetails?.wine?.yeasts },
                                            { label: "Cultivar", value: selectedDetails?.wine?.cultivar },
                                            { label: "Filtrazione", value: selectedDetails?.wine?.filtration },
                                            { label: "Promozione", value: selectedDetails?.wine?.promotion },
                                            { label: "SO2", value: selectedDetails?.wine?.so2 },
                                            { label: "Disp. Netta", value: selectedDetails?.wine?.net_availability },
                                            { label: "Disp. Lorda", value: selectedDetails?.wine?.gross_availability }
                                        ].map((detail, idx) => {
                                            const displayValue = (val) => {
                                                if (val === null || val === undefined || val === "") return "N/A";
                                                return val;
                                            };
                                            const valStr = displayValue(detail.value);
                                            return (
                                                <div key={idx} className="bg-gray-50 p-2 rounded-lg border border-gray-100 flex flex-col justify-center">
                                                    <span className="text-gray-500 block text-[10px] uppercase tracking-wider mb-0.5">{detail.label}</span>
                                                    <span className="font-medium text-gray-800 text-xs truncate" title={valStr}>{valStr}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Wine Confirmation Modal */}
            <ConfirmationDialog
                isOpen={Boolean(wineToDelete)}
                title="Are you sure you want to delete the Wine?"
                onConfirm={confirmDeleteWine}
                onCancel={() => !isDeleting && setWineToDelete(null)}
                confirmText={isDeleting ? "Deleting..." : "Yes"}
                cancelText="No"
            />

            {/* Edit Wine Quantity Modal */}
            <EditWineQuantityModal
                isOpen={Boolean(wineToEdit)}
                wineItem={wineToEdit}
                onClose={() => !isUpdatingQuantity && setWineToEdit(null)}
                onSave={handleSaveWineQuantity}
                isSaving={isUpdatingQuantity}
            />
        </div>
    );
};

ProposalPreview.propTypes = {
    hasPermission: PropTypes.func,
};

export default ProposalPreview;
